// Browser milestones are not the server's lender-matched lead conversion.
export class OpenaiAdsJourney {
	static readonly PIXEL_ID = 'YEsaNYXE1T2981Mou5PREJ';
	private readonly stages = new Set<string>();
	private readonly documentId: string;
	private readonly attributed: boolean;
	private initialized = false;
	private emailVersion = 0;
	private emailKey = '';
	private emailHash = '';
	private emailPending: Promise<void> | null = null;
	private visitorVersion = 0;
	private visitorKey = '';
	private visitorHash = '';
	private visitorPending: Promise<void> | null = null;
	private userSnapshot = '{}';

	constructor(private readonly browser: Window, private readonly page: Document) {
		this.attributed = this.isAttributed();
		let id = '';
		try { id = browser.crypto?.randomUUID?.() || ''; } catch (_) { /* Fail closed. */ }
		this.documentId = id;
	}

	pageViewed(visitorReady = false): void {
		try {
			if (!this.allowed()) { return; }
			// Hope creates its existing visitor cookie during init, after service construction.
			if (!visitorReady && !this.visitorId()
				&& new URL(this.browser.location.href).searchParams.get('startAssistant') === '1') { return; }
			this.measure('page_viewed');
		} catch (_) { /* Non-fatal. */ }
	}

	committed(step: string, value: unknown, contact?: { firstName?: unknown; lastName?: unknown }): void {
		if (!step || value === null || value === undefined || value === '') { return; }
		// A start is the first accepted answer; page_viewed counts passive arrivals.
		this.measure('form_started');
		if (step === 'loanPurpose') { this.measure('loan_type_selected'); }
		if (['firstName', 'lastName', 'firstNameLastName'].includes(step)
			&& this.validName(contact?.firstName) && this.validName(contact?.lastName)) {
			this.measure('name_provided');
		}
		if (step === 'email') { void this.emailProvided(value); }
	}

	prepareSubmission(email: unknown): void {
		void this.prepareEmail(email);
	}

	submitted(leadId: unknown, email?: unknown): void {
		try {
			if (!/^[1-9][0-9]*$/.test(String(leadId)) || !this.allowed()) { return; }
			if (email !== undefined) { this.prepareSubmission(email); }
			const version = this.emailVersion;
			if (this.emailPending) {
				void this.emailPending.then(() => {
					if (version === this.emailVersion) { this.measure('form_submitted'); }
				}).catch(() => { /* Never interrupt submission or navigation. */ });
			} else { this.measure('form_submitted'); }
		} catch (_) { /* Non-fatal. */ }
	}

	private isAttributed(): boolean {
		try {
			const source = new URL(this.browser.location.href).searchParams.get('utm_source');
			if (source !== null) { return source === 'chatgptads'; }
			const cookie = this.page.cookie.split(';').find(part => part.trim().startsWith('lead_referrer='));
			return !!cookie && JSON.parse(decodeURIComponent(cookie.trim().slice(14))).source === 'chatgptads';
		} catch (_) { return false; }
	}

	private allowed(): boolean {
		const win = this.browser as any;
		const denied = !!win.removeTrackers || this.browser.navigator.doNotTrack === '1'
			|| win.navigator.globalPrivacyControl === true
			|| this.page.cookie.split(';').some(part => part.trim().split('=')[0] === 'fruRemoveCookies');
		if (denied) {
			try { if (typeof win.oaiq === 'function') { win.oaiq('consent', false); } } catch (_) { /* Non-fatal. */ }
			this.emailVersion += 1;
			this.emailKey = '';
			this.emailHash = '';
			this.emailPending = null;
			this.visitorVersion += 1;
			this.visitorKey = '';
			this.visitorHash = '';
			this.visitorPending = null;
			return false;
		}
		return this.attributed && !!this.documentId && this.browser.location.protocol === 'https:'
			&& ['www.freerateupdate.com', 'freerateupdate.com'].includes(this.browser.location.hostname)
			&& /^\/communication\/rates-form\/?$/.test(this.browser.location.pathname);
	}

	private initialize(): boolean {
		if (!this.allowed()) { return false; }
		if (this.initialized) { this.updateUser(); return true; }
		const win = this.browser as any;
		if (win.oaiq !== undefined && typeof win.oaiq !== 'function') { return false; }
		if (!win.oaiq) {
			const queue: any = function (...args: any[]) { queue.q.push(args); };
			queue.q = [];
			win.oaiq = queue;
			const script = this.page.createElement('script');
			script.async = true;
			script.src = 'https://bzrcdn.openai.com/sdk/oaiq.min.js';
			this.page.head.appendChild(script);
		}
		const user = this.matchingUser();
		win.oaiq('init', { pixelId: OpenaiAdsJourney.PIXEL_ID,
			...(Object.keys(user).length ? { user } : {}) });
		this.userSnapshot = JSON.stringify(user);
		this.initialized = true;
		return true;
	}

	private measure(stage: string, eventId?: string): void {
		try {
			if (this.stages.has(stage) || !this.allowed()) { return; }
			const visitor = this.visitorId();
			if (visitor && (visitor !== this.visitorKey || this.visitorPending)) {
				const pending = this.prepareVisitor(visitor);
				const version = this.visitorVersion;
				void pending.then(() => {
					if (version === this.visitorVersion && this.visitorId() === visitor) {
						this.dispatch(stage, eventId);
					}
				}).catch(() => { /* Never interrupt the form. */ });
				return;
			}
			this.dispatch(stage, eventId);
		} catch (_) { /* Measurement must not block answering or submission. */ }
	}

	private dispatch(stage: string, eventId?: string): void {
		try {
			if (this.stages.has(stage) || !this.initialize()) { return; }
			const win = this.browser as any;
			const options: any = { event_id: eventId || this.documentId + '-' + stage };
			if (stage !== 'page_viewed') { options.custom_event_name = stage; }
			win.oaiq('measureSingle', OpenaiAdsJourney.PIXEL_ID, stage === 'page_viewed' ? stage : 'custom',
				{ type: stage === 'page_viewed' ? 'contents' : 'custom' }, options);
			this.stages.add(stage);
		} catch (_) { /* Measurement must not block answering or submission. */ }
	}

	private visitorId(): string {
		try {
			const cookie = this.page.cookie.split(';').find(part => part.trim().startsWith('fru_vid='));
			const value = cookie ? decodeURIComponent(cookie.trim().slice(8)).trim() : '';
			// Only the two existing FRU returning-visitor shapes; never mint a matching ID here.
			return /^(?:[0-9a-fA-F]{32}|[0-9a-fA-F]{8}(?:-[0-9a-fA-F]{4}){3}-[0-9a-fA-F]{12})$/.test(value)
				? value : '';
		} catch (_) { return ''; }
	}

	private prepareVisitor(visitor: string): Promise<void> {
		if (visitor === this.visitorKey) { return this.visitorPending || Promise.resolve(); }
		this.visitorKey = visitor;
		this.visitorHash = '';
		const version = ++this.visitorVersion;
		this.visitorPending = Promise.resolve().then(async () => {
			try {
				if (version !== this.visitorVersion || !this.allowed() || this.visitorId() !== visitor) { return; }
				const digest = await this.browser.crypto.subtle.digest('SHA-256', new TextEncoder().encode(visitor));
				if (version !== this.visitorVersion || !this.allowed() || this.visitorId() !== visitor) { return; }
				this.visitorHash = Array.from(new Uint8Array(digest), byte => byte.toString(16).padStart(2, '0')).join('');
			} catch (_) { /* Unavailable hashing never falls back to the raw cookie. */ }
			finally { if (version === this.visitorVersion) { this.visitorPending = null; } }
		});
		return this.visitorPending;
	}

	private matchingUser(): Record<string, string> {
		return {
			...(this.emailHash ? { email_sha256: this.emailHash } : {}),
			...(this.visitorHash && this.visitorKey === this.visitorId()
				? { external_id_sha256: this.visitorHash } : {}),
		};
	}

	private updateUser(): void {
		const user = this.matchingUser();
		const snapshot = JSON.stringify(user);
		if (snapshot === this.userSnapshot) { return; }
		(this.browser as any).oaiq('init', { pixelId: OpenaiAdsJourney.PIXEL_ID, user });
		this.userSnapshot = snapshot;
	}

	private validName(value: unknown): boolean {
		return typeof value === 'string' && value.trim().length > 0
			&& !['provided', 'committed'].includes(value.trim().toLowerCase());
	}

	private prepareEmail(value: unknown): Promise<void> {
		try {
			if (!this.allowed()) { return Promise.resolve(); }
			const normalized = typeof value === 'string' ? value.trim().toLowerCase() : '';
			const key = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(normalized) ? normalized : '';
			if (key === this.emailKey) { return this.emailPending || Promise.resolve(); }
			this.emailKey = key;
			this.emailHash = '';
			const version = ++this.emailVersion;
			if (!key) { this.emailPending = null; return Promise.resolve(); }
			this.emailPending = Promise.resolve().then(async () => {
				try {
					if (version !== this.emailVersion || !this.allowed()) { return; }
					const digest = await this.browser.crypto.subtle.digest('SHA-256', new TextEncoder().encode(key));
					if (version !== this.emailVersion || !this.allowed()) { return; }
					this.emailHash = Array.from(new Uint8Array(digest), byte => byte.toString(16).padStart(2, '0')).join('');
				} catch (_) { /* Unavailable hashing never falls back to a raw or stale email. */ }
				finally { if (version === this.emailVersion) { this.emailPending = null; } }
			});
			return this.emailPending;
		} catch (_) { return Promise.resolve(); }
	}

	private async emailProvided(value: unknown): Promise<void> {
		try {
			const pending = this.prepareEmail(value);
			const version = this.emailVersion;
			await pending;
			if (version === this.emailVersion && this.emailHash && this.allowed()) {
				this.measure('email_provided');
			}
		} catch (_) { /* Non-fatal. */ }
	}
}
